import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import { Avatar, AvatarGroup, Box } from '@chakra-ui/react';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { useController } from 'pages/Meetings/hooks/useController';
import { formatShortDate } from 'utils/dateFormat';

import { useAlerts } from 'hooks/useAlerts';
import { useUser } from 'hooks/useUser';

import cancelInvite from 'api/matchmaking/cancelInvite';
import sendProspectionInvite from 'api/matchmaking/sendProspectionInvite';

import BoxInfo from 'components/atom/BoxInfo';
import Footer from 'components/atom/Footer';
import Button from 'components/atom/Form/Button';
import { Wrapper } from 'components/atom/Grid';
import SpeechBubble from 'components/atom/SpeechBubble';
import Text from 'components/atom/Text/styles';
import HeaderContent from 'components/molecule/HeaderContent';

import ModalSuccessInvite from './components/ModalSuccessInvite';
import { ConfirmedContainer, Container, Content, PreviewInformation, Title } from './styles';

const PreviewInvitation: React.FC = () => {
  const { t } = useTranslation();
  const { errorMessage } = useAlerts();

  const { user } = useUser();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const {
    setPageActive,
    prospectionData,
    invitationDataSuggested,
    setInvitationDataSuggested,

    formik: { resetForm, values },
  } = useController();

  const handleClose = useCallback(() => {
    setPageActive('schedule');
    resetForm();
    setInvitationDataSuggested(null);
  }, [resetForm, setInvitationDataSuggested, setPageActive]);

  const sendProspectionInviteMutation = useMutation(sendProspectionInvite);

  const cancelInviteMutation = useMutation(cancelInvite);

  const handleSave = useCallback(async () => {
    const prepareData = {
      userId: Number(prospectionData?.user.id || 0),
      message: values.message,
      dates: [`${values.date} ${values.hour}`],
    };

    if (invitationDataSuggested?.matchId) {
      await cancelInviteMutation.mutateAsync({ matchId: Number(invitationDataSuggested?.matchId) });
    }

    const res = await sendProspectionInviteMutation.mutateAsync({ ...prepareData });

    if (!res) {
      errorMessage(t('meetings:messages.errorInvite'));
    } else {
      resetForm();
      setIsModalOpen(true);
    }

    setInvitationDataSuggested(null);
  }, [
    cancelInviteMutation,
    errorMessage,
    invitationDataSuggested?.matchId,
    prospectionData?.user.id,
    resetForm,
    sendProspectionInviteMutation,
    setInvitationDataSuggested,
    t,
    values.date,
    values.hour,
    values.message,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Container>
      <HeaderContent title={t('meetings:preview.title')} onClose={handleClose} />

      <Content>
        <Wrapper>
          <Text p16 padding="0 0 3.5rem">
            {t('meetings:preview.info')}
          </Text>
        </Wrapper>

        <PreviewInformation>
          <Wrapper>
            <AvatarGroup size="xl" max={2}>
              <Avatar name={user.fullname} src={user.avatar} />
              <Avatar name={prospectionData?.user.fullname} src={prospectionData?.user.avatar} />
            </AvatarGroup>

            <Title>
              {t('meetings:meeting.meetingWith')} <strong>{prospectionData?.user.fullname}</strong>
            </Title>

            <SpeechBubble message={values?.message} />

            <Box mt={8} width="100%">
              <BoxInfo
                label={t('meetings:preview.when')}
                info={
                  values?.date &&
                  values.hour &&
                  `${formatShortDate(values?.date)} - ${values?.hour}`
                }
                hideBorder
              />

              <BoxInfo label={`${t('meetings:meeting.local')}:`} info="SET Centro-Oeste" />
            </Box>

            <ConfirmedContainer>
              <Text weight="bold">{t('meetings:preview.question')}</Text>

              <Button
                theme="blue03"
                text={t('meetings:actions.sendInvitation')}
                icon={faCheck}
                disabledUppercase
                onClick={handleSave}
                isLoading={
                  sendProspectionInviteMutation.isLoading || cancelInviteMutation.isLoading
                }
              />
            </ConfirmedContainer>
          </Wrapper>
        </PreviewInformation>
      </Content>

      <Footer />

      <ModalSuccessInvite isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </Container>
  );
};

export default PreviewInvitation;
